import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { useModalContext } from "../../../contexts/ModalContext";
import * as MechanicActivityOffersService from "../../../services/mechanicActivityOffers";
import { Container, FormContainer } from "./styles";

const VALIDITY_PRESET = {
    DAYS_15: "15d",
    DAYS_30: "30d",
    MONTHS_3: "3m",
    CUSTOM: "custom",
};

function localDateISO(d = new Date()) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/** Calendar arithmetic at noon to reduce DST edge cases; returns YYYY-MM-DD in local time. */
function addCalendarDays(base, days) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days, 12, 0, 0, 0);
    return localDateISO(d);
}

function addCalendarMonths(base, months) {
    const d = new Date(base.getFullYear(), base.getMonth() + months, base.getDate(), 12, 0, 0, 0);
    return localDateISO(d);
}

function validUntilFromPreset(preset, fromDate = new Date()) {
    if (preset === VALIDITY_PRESET.DAYS_15) return addCalendarDays(fromDate, 15);
    if (preset === VALIDITY_PRESET.DAYS_30) return addCalendarDays(fromDate, 30);
    if (preset === VALIDITY_PRESET.MONTHS_3) return addCalendarMonths(fromDate, 3);
    return null;
}

function parseApiError(error) {
    const d = error?.response?.data;
    if (typeof d === "string") return d;
    if (d?.message) return d.message;
    if (Array.isArray(d?.errors)) return d.errors.map((e) => e.defaultMessage || e.message).filter(Boolean).join(" ");
    return "Não foi possível salvar a oferta.";
}

const OfferFormModal = ({ offer, activities, occupiedActivityIds, onSaved }) => {
    const { setModal } = useModalContext();
    const isEdit = Boolean(offer);
    const minDate = localDateISO();

    const availableActivities = useMemo(() => {
        if (!activities?.length) return [];
        if (isEdit) return activities;
        return activities.filter((a) => !occupiedActivityIds.has(a.id));
    }, [activities, occupiedActivityIds, isEdit]);

    const [selectedActivity, setSelectedActivity] = useState(null);
    const [discountPercent, setDiscountPercent] = useState("");
    const [validityPreset, setValidityPreset] = useState(VALIDITY_PRESET.DAYS_30);
    const [customValidUntil, setCustomValidUntil] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const presetPreviewUntil = useMemo(
        () => validUntilFromPreset(validityPreset, new Date()) || "",
        [validityPreset]
    );

    useEffect(() => {
        if (isEdit && offer) {
            const match = activities.find((a) => a.id === offer.activityId) || {
                id: offer.activityId,
                name: offer.activityName,
            };
            setSelectedActivity(match);
            setDiscountPercent(String(offer.discountPercent ?? ""));
            setValidityPreset(VALIDITY_PRESET.CUSTOM);
            setCustomValidUntil(offer.validUntil ? String(offer.validUntil).slice(0, 10) : "");
        } else {
            setSelectedActivity(null);
            setDiscountPercent("");
            setValidityPreset(VALIDITY_PRESET.DAYS_30);
            setCustomValidUntil("");
        }
    }, [isEdit, offer, activities]);

    const handleClose = () => setModal(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const discount = parseInt(String(discountPercent).trim(), 10);
        if (Number.isNaN(discount) || discount < 1 || discount > 100) {
            alert("Informe um desconto entre 1 e 100%.");
            return;
        }
        const validUntil =
            validityPreset === VALIDITY_PRESET.CUSTOM ? customValidUntil : validUntilFromPreset(validityPreset, new Date());
        if (!validUntil || validUntil < minDate) {
            alert("A data de validade deve ser hoje ou uma data futura.");
            return;
        }
        const activityId = isEdit ? offer.activityId : selectedActivity?.id;
        if (!activityId) {
            alert("Selecione uma atividade.");
            return;
        }
        setSubmitting(true);
        try {
            await MechanicActivityOffersService.saveOffer({
                activityId,
                discountPercent: discount,
                validUntil,
            });
            alert(isEdit ? "Oferta atualizada com sucesso." : "Oferta criada com sucesso.");
            setModal(null);
            onSaved();
        } catch (err) {
            alert(parseApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open onClose={handleClose} aria-labelledby="offer-form-title">
            <Container>
                <Box
                    mb={2}
                    fontWeight="bold"
                    color="#666666"
                    fontSize={{ xs: "h7.fontSize", sm: "h7.fontSize", md: "h6.fontSize", xl: "h5.fontSize" }}
                    id="offer-form-title"
                >
                    {isEdit ? "Editar oferta" : "Nova oferta"}
                </Box>
                <form onSubmit={handleSubmit}>
                    <FormContainer>
                        {isEdit ? (
                            <TextField label="Atividade" value={offer.activityName || ""} disabled fullWidth variant="outlined" />
                        ) : (
                            <>
                                {availableActivities.length === 0 ? (
                                    <Typography color="text.secondary">
                                        Todas as atividades do catálogo já possuem oferta. Edite uma oferta na tabela ou remova uma para
                                        cadastrar outra.
                                    </Typography>
                                ) : (
                                    <Autocomplete
                                        disablePortal
                                        options={availableActivities}
                                        getOptionLabel={(option) => option.name || ""}
                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                        value={selectedActivity}
                                        onChange={(_, v) => setSelectedActivity(v)}
                                        renderInput={(params) => <TextField {...params} required label="Atividade" />}
                                    />
                                )}
                            </>
                        )}
                        <TextField
                            label="Desconto (%)"
                            type="number"
                            required
                            fullWidth
                            value={discountPercent}
                            onChange={(ev) => setDiscountPercent(ev.target.value)}
                            inputProps={{ min: 1, max: 100, step: 1 }}
                            helperText="Valor inteiro entre 1 e 100."
                        />
                        <FormControl component="fieldset" variant="standard" fullWidth sx={{ minWidth: 0 }}>
                            <FormLabel component="legend" sx={{ color: "#666", fontWeight: 600, mb: 0.5 }}>
                                Válido por:
                            </FormLabel>
                            <RadioGroup
                                value={validityPreset}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setValidityPreset(v);
                                    if (v === VALIDITY_PRESET.CUSTOM && !customValidUntil) {
                                        setCustomValidUntil(validUntilFromPreset(VALIDITY_PRESET.DAYS_30, new Date()));
                                    }
                                }}
                            >
                                <FormControlLabel value={VALIDITY_PRESET.DAYS_15} control={<Radio size="small" />} label="15 dias" />
                                <FormControlLabel value={VALIDITY_PRESET.DAYS_30} control={<Radio size="small" />} label="30 dias" />
                                <FormControlLabel value={VALIDITY_PRESET.MONTHS_3} control={<Radio size="small" />} label="3 meses" />
                                <FormControlLabel value={VALIDITY_PRESET.CUSTOM} control={<Radio size="small" />} label="Personalizado" />
                            </RadioGroup>
                            {validityPreset !== VALIDITY_PRESET.CUSTOM && presetPreviewUntil && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5, maxWidth: "100%", wordBreak: "break-word" }}
                                >
                                    Válida inclusive até {presetPreviewUntil.split("-").reverse().join("/")} (calculado a partir de hoje).
                                </Typography>
                            )}
                        </FormControl>
                        {validityPreset === VALIDITY_PRESET.CUSTOM && (
                            <TextField
                                label="Válida até"
                                type="date"
                                required
                                fullWidth
                                value={customValidUntil}
                                onChange={(ev) => setCustomValidUntil(ev.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: minDate }}
                                helperText="Válida inclusive neste dia. Datas no passado são rejeitadas pelo servidor."
                            />
                        )}
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            flexWrap="wrap"
                            gap={1}
                            mt={1}
                            sx={{ minWidth: 0, "& .MuiButton-root": { flexShrink: 0 } }}
                        >
                            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" variant="contained" disabled={submitting || (!isEdit && availableActivities.length === 0)}>
                                Salvar
                            </Button>
                        </Box>
                    </FormContainer>
                </form>
            </Container>
        </Modal>
    );
};

export default OfferFormModal;
